import {getClaimsForMark} from '../../../../../components/prosemirror-editor/yMaps/claimsYMap';
import {extractReferencesFromYDoc} from '../../../../../utils/yjsSnapshot';
import {resolveIcrNumber} from '../../../../../firebase/firestore/icr';
import {resolvePirEeis} from '../../../../collection-requests/pirPayload';

/**
 * Resolve a coverage requirementId to a human-readable ICR/EEI identifier.
 * requirementId format: `{icrId}:q-icr` or `{icrId}:q-eei-{eeiId}`.
 */
export function resolveRequirementDisplayId(requirementId, icr = null) {
    if (!requirementId) {
        return 'req';
    }
    const suffix = requirementId.includes(':')
        ? requirementId.split(':').slice(1).join(':')
        : requirementId;

    if (suffix === 'q-icr') {
        return resolveIcrNumber(icr) || icr?.id || requirementId.split(':')[0] || 'ICR';
    }

    const eeiMatch = suffix.match(/^q-eei-(.+)$/);
    if (eeiMatch) {
        const eeiId = eeiMatch[1];
        const eeis = resolvePirEeis(icr);
        const eei = eeis.find((e) => String(e.id) === String(eeiId));
        return eei?.eeiNumber || eeiId;
    }

    return suffix;
}

/**
 * Collect active claims from all reference marks in the live Y.Doc.
 */
export function collectLiveClaims(yReferenceMarks, reportId) {
    if (!yReferenceMarks || !reportId) {
        return [];
    }
    const claims = [];
    yReferenceMarks.forEach((markMap, referenceMarkId) => {
        if (!referenceMarkId || referenceMarkId.startsWith('__')) {
            return;
        }
        const {claims: markClaims} = getClaimsForMark(yReferenceMarks, referenceMarkId);
        markClaims.forEach(claim => {
            claims.push({
                id: `${referenceMarkId}-${claim.id ?? claims.length + 1}`,
                globalId: `${reportId}-${referenceMarkId}-${claim.id ?? claims.length + 1}`,
                claim: claim.claim,
                status: claim.status || 'not_proven',
                referenceMarkId,
                supportingEvidence: claim.supportingEvidence || claim.supporting_evidence || [],
            });
        });
    });
    return claims;
}

/**
 * Build POST body for draft coverage v2 from editor + Yjs state.
 */
export function buildDraftCoveragePayload({editorView, ydoc, yReferenceMarks, reportId}) {
    const content = editorView?.state?.doc?.toJSON?.() || null;
    const liveClaims = collectLiveClaims(yReferenceMarks, reportId);
    const referenceMarks = ydoc ? extractReferencesFromYDoc(ydoc) : [];
    const contentHash = content
        ? String(JSON.stringify({content, liveClaims}).length)
        : '';
    return {content, liveClaims, referenceMarks, contentHash};
}

/**
 * Map v2 requirement DTOs to TopicGroup card items.
 */
export function draftRequirementsToTopicItems(requirements = [], icr = null) {
    return requirements.map(req => {
        const displayId = resolveRequirementDisplayId(req.requirementId, icr);
        const addressedPct = req.coverageScore != null
            ? Math.round(Number(req.coverageScore) * 100)
            : Math.round(Number(req.factualPct || 0) + Number(req.opinionPct || 0));
        const gap = req.gapReason || '';
        return {
            tag: req.text,
            requirementId: req.requirementId,
            comment: `${displayId} · ${addressedPct}% addressed${gap ? ` — ${gap}` : ''}`,
        };
    });
}
